import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import {
  CheckCircle,
  ArrowLeft,
  CreditCard,
  Shield,
  Clock,
  Users,
  Loader2,
  Phone,
  Smartphone,
} from "lucide-react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
// import ProcessingPaymentModal from "./ProcessingPaymentModal";
import Images from "../../utils/Images";
import subscriptionRequests from "../../api/Customer/SubscriptionRequests";

const PaymentConfirmation = ({
  selectedPlan,
  onBack,
  onProceedToPayment,
  selectedPackageName,
  selectedDuration,
}) => {
  const user = useSelector((state) => state.user?.txtconnect_ng_user);
  // const subscriptionPlan = useSelector((state) => state.user?.txtconnect_ng_user?.subscription_plan);
  const [loading, setLoading] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  // const [isProcessingOpen, setIsProcessingOpen] = useState(false);
  // const [currentPayload, setCurrentPayload] = useState(null);

  // Convert 233 format to 0 format for display
  const convertPhoneToLocalFormat = (phone) => {
    if (!phone) return "";

    // Remove any spaces or special characters
    const cleaned = phone.replace(/\D/g, "");

    // If starts with 233 (12 digits), remove 233 and add 0 (should result in 10 digits: 0 + 9 digits)
    if (cleaned.startsWith("233") && cleaned.length >= 12) {
      const localNumber = cleaned.substring(3);
      if (localNumber.length === 9) {
        return "0" + localNumber;
      } else if (localNumber.length > 9) {
        return "0" + localNumber.substring(0, 9);
      }
      return "";
    }

    // If already starts with 0 and has 10 digits (0 + 9), return as is
    if (cleaned.startsWith("0") && cleaned.length === 10) {
      return cleaned;
    }

    // If already starts with 0 but has more digits, truncate to 10
    if (cleaned.startsWith("0") && cleaned.length > 10) {
      return cleaned.substring(0, 10);
    }

    // If it's 9 digits without prefix, add 0
    if (cleaned.length === 9) {
      return "0" + cleaned;
    }

    // Default: return first 10 digits if available
    return cleaned.substring(0, 10);
  };

  // Network configuration with placeholders for images
  const networks = [
    {
      id: "mtn",
      name: "MTN",
      color: "yellow",
      image: Images.mtn_momo,
    },
    {
      id: "telecel",
      name: "Telecel",
      color: "red",
      image: Images.telecel_cash,
      // Add your image: image: "/path/to/telecel-logo.png"
    },
    {
      id: "airteltigo",
      name: "AirtelTigo",
      color: "red",
      image: Images.at_cash,
    },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-populate phone number from user account when component mounts or user changes
  useEffect(() => {
    if (user?.phone_number && !phoneNumber) {
      const localFormat = convertPhoneToLocalFormat(user.phone_number);
      if (localFormat && localFormat.length === 10) {
        setPhoneNumber(localFormat);
        setPhoneError("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.phone_number]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GH", {
      style: "currency",
      currency: "GHS",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  //console.log("selected Plan", selectedPlan);

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 10) {
      setPhoneNumber(value);
      if (value.length === 10) {
        setPhoneError("");
      } else if (value.length > 0) {
        setPhoneError(`Phone number must be 10 digits (${value.length}/10)`);
      } else {
        setPhoneError("");
      }
    }
  };

  const validateForm = () => {
    if (!selectedNetwork) {
      toast.error("Please select a network");
      return false;
    }
    if (phoneNumber.length !== 10) {
      toast.error("Phone number must be exactly 10 digits");
      setPhoneError("Phone number must be exactly 10 digits");
      return false;
    }
    return true;
  };

  const handleProceedToPayment = async () => {
    if (!validateForm()) return;
    if (loading) return;

    setLoading(true);
    try {
      const duration = selectedDuration || selectedPlan.duration || "1 month";
      const networkValue =
        selectedNetwork === "mtn"
          ? "MTN"
          : selectedNetwork === "telecel"
          ? "VDF"
          : selectedNetwork === "airteltigo"
          ? "ATL"
          : selectedNetwork;
//change pricing
      const payload = {
        network: networkValue,
        package_name: selectedPackageName,
        plan_name: duration,
        price: selectedPlan.price,
        // price: 0.1,
        units: selectedPlan.unit,
        phone_number: `233${
          phoneNumber.startsWith("0") ? phoneNumber.slice(1) : phoneNumber
        }`,
      };

      const result = await subscriptionRequests.initiateSubscriptionPayment(payload);

      if (result.status_code === "000" && result.data?.payment_link) {
        window.location.href = result.data.payment_link;
      } else {
        toast.error(result.reason || "Failed to initiate payment. Please try again.");
        setLoading(false);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to process payment. Please try again."
      );
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="pt-0 pb-6 bg-gradient-to-br from-gray-50 via-blue-50 to-red-50"
    >
      <div className="px-4 py-0 mx-auto max-w-8xl sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-4">
          <div className="mb-4 text-center">
            <h1 className="mb-2 text-2xl font-bold text-gray-800">
              Confirm Your Selection
            </h1>
            <p className="text-sm text-gray-600">
              Review your plan details and enter payment information
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Selected Plan Card */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="overflow-hidden bg-white rounded-xl border-2 border-red-600 shadow-lg"
            >
              {/* Plan Header */}
              <div className="p-4 text-white bg-gradient-to-r from-red-600 to-red-700">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-xl font-bold">
                    {selectedPlan.name} Plan
                  </h2>
                  <div className="p-1.5 bg-white bg-opacity-20 rounded-lg">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                </div>
                <div className="flex gap-2 items-baseline">
                  <span className="text-3xl font-bold">
                    {formatCurrency(selectedPlan.price)}
                  </span>
                </div>
              </div>

              {/* Plan Features */}
              <div className="p-4">
                <h3 className="mb-3 text-lg font-semibold text-gray-800">
                  What's Included:
                </h3>

                <div className="space-y-3 mb-4">
                  <div className="flex gap-3 items-center">
                    <div className="flex justify-center items-center w-10 h-10 bg-red-100 rounded-full flex-shrink-0">
                      <Users className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800">
                        {formatNumber(selectedPlan.unit)} SMS Units
                      </p>
                      <p className="text-xs text-gray-600">Allocation</p>
                    </div>
                  </div>

                  <div className="flex gap-3 items-center">
                    <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-full flex-shrink-0">
                      <Shield className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800">
                        Unlimited Contact Groups
                      </p>
                      <p className="text-xs text-gray-600">
                        Organize your contacts
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3 items-center">
                    <div className="flex justify-center items-center w-10 h-10 bg-orange-100 rounded-full flex-shrink-0">
                      <Clock className="w-5 h-5 text-orange-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800">
                        24/7 Support
                      </p>
                      <p className="text-xs text-gray-600">
                        Always here to help
                      </p>
                    </div>
                  </div>
                </div>

                {/* Additional Benefits */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                    <span className="flex gap-1 items-center">
                      <CheckCircle className="w-3 h-3 text-red-500 flex-shrink-0" />
                      Instant activation
                    </span>
                    <span className="flex gap-1 items-center">
                      <CheckCircle className="w-3 h-3 text-red-500 flex-shrink-0" />
                      No hidden fees
                    </span>
                    <span className="flex gap-1 items-center">
                      <CheckCircle className="w-3 h-3 text-red-500 flex-shrink-0" />
                      Secure payment
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Payment Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="sticky top-16 p-4 bg-white rounded-xl border border-gray-200 shadow-lg max-h-[calc(100vh-6rem)] overflow-y-auto"
            >
              {/* Back to Plans Button */}
              <button
                onClick={onBack}
                className="flex gap-2 items-center px-3 py-1.5 mb-4 text-sm text-gray-600 bg-gray-50 rounded-lg border border-gray-200 transition-all duration-200 hover:bg-gray-100 hover:text-gray-900 hover:shadow-sm hover:border-gray-300"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="font-medium">Back to Plans</span>
              </button>

              <h3 className="mb-4 text-lg font-semibold text-gray-800">
                Payment Details
              </h3>

              {/* Network Selection */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-medium text-gray-700">
                  Select Network <span className="text-red-600">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {networks.map((network) => (
                    <motion.button
                      key={network.id}
                      onClick={() => setSelectedNetwork(network.id)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`relative p-2 rounded-lg border-2 transition-all duration-300 overflow-hidden ${
                        selectedNetwork === network.id
                          ? "border-red-600 bg-gradient-to-br from-red-50 to-red-100 shadow-md ring-2 ring-red-200 ring-opacity-50"
                          : "border-gray-200 bg-white hover:border-red-300 hover:shadow-sm hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex flex-col gap-1.5 items-center">
                        {/* Network Image with enhanced styling */}
                        {network.image ? (
                          <div
                            className={`relative flex justify-center items-center w-10 h-10 p-1.5 bg-white rounded-lg transition-all duration-300 ${
                              selectedNetwork === network.id
                                ? "shadow-sm ring-1 ring-red-200"
                                : "shadow-sm"
                            }`}
                          >
                            <img
                              src={network.image}
                              alt={network.name}
                              className="w-full h-full object-contain"
                            />
                          </div>
                        ) : (
                          <div
                            className={`flex justify-center items-center w-10 h-10 p-1.5 bg-gray-50 rounded-lg ${
                              selectedNetwork === network.id ? "bg-red-50" : ""
                            }`}
                          >
                            <Smartphone
                              className={`w-6 h-6 transition-colors duration-300 ${
                                selectedNetwork === network.id
                                  ? "text-red-600"
                                  : "text-gray-400"
                              }`}
                            />
                          </div>
                        )}
                        <span
                          className={`text-xs font-semibold transition-colors duration-300 ${
                            selectedNetwork === network.id
                              ? "text-red-700"
                              : "text-gray-600"
                          }`}
                        >
                          {network.name}
                        </span>
                      </div>
                      {/* Selection indicator */}
                      {selectedNetwork === network.id && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="absolute top-0.5 right-0.5"
                        >
                          <div className="flex justify-center items-center w-4 h-4 bg-red-600 rounded-full">
                            <CheckCircle className="w-2.5 h-2.5 text-white" />
                          </div>
                        </motion.div>
                      )}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Phone Number Input */}
              <div className="mb-4">
                <label className="block mb-1.5 text-xs font-medium text-gray-700">
                  Phone Number <span className="text-red-600">*</span>
                </label>
                <div className="relative">
                  <div className="flex absolute inset-y-0 left-0 items-center pl-2.5 pointer-events-none">
                    <Phone
                      className={`w-4 h-4 transition-colors duration-300 ${
                        phoneNumber.length === 10
                          ? "text-green-600"
                          : "text-gray-400"
                      }`}
                    />
                  </div>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    placeholder="0241234567"
                    maxLength={10}
                    className={`w-full pl-9 pr-10 py-2 text-sm rounded-lg border-2 transition-all duration-300 focus:outline-none ${
                      phoneNumber.length === 10
                        ? "border-green-500 bg-green-50 focus:border-green-600 focus:ring-2 focus:ring-green-200"
                        : phoneError && phoneNumber.length > 0
                        ? "border-red-300 focus:border-red-500 bg-red-50"
                        : "border-gray-200 focus:border-red-600 focus:ring-2 focus:ring-red-100"
                    }`}
                  />
                  {/* Green check icon when valid */}
                  {phoneNumber.length === 10 && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="absolute inset-y-0 right-0 flex items-center pr-2.5 pointer-events-none"
                    >
                      <div className="flex justify-center items-center w-6 h-6 bg-green-500 rounded-full shadow-sm">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                    </motion.div>
                  )}
                </div>
                {phoneError &&
                  phoneNumber.length > 0 &&
                  phoneNumber.length !== 10 && (
                    <p className="mt-1 text-xs text-red-600">{phoneError}</p>
                  )}
                {phoneNumber.length === 10 && (
                  <motion.p
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-1 text-xs font-medium text-green-600"
                  >
                    ✓ Valid phone number
                  </motion.p>
                )}
                {phoneNumber.length === 0 && (
                  <p className="mt-0.5 text-xs text-gray-500">
                    Enter 10-digit phone number
                  </p>
                )}
              </div>

              {/* Payment Summary */}
              <div className="pt-3 mb-4 space-y-2 border-t border-gray-200">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Plan</span>
                  <span className="font-medium text-gray-800">
                    {selectedPlan.name}
                  </span>
                </div>

                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">SMS Units</span>
                  <span className="font-medium text-gray-800">
                    {formatNumber(selectedPlan.unit)}
                  </span>
                </div>

                {selectedNetwork && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Network</span>
                    <span className="font-medium text-gray-800 capitalize">
                      {networks.find((n) => n.id === selectedNetwork)?.name}
                    </span>
                  </div>
                )}

                {phoneNumber.length === 10 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Phone</span>
                    <span className="font-medium text-gray-800">
                      +233 {phoneNumber}
                    </span>
                  </div>
                )}

                <hr className="border-gray-200" />

                <div className="flex justify-between items-center">
                  <span className="text-base font-semibold text-gray-800">
                    Total
                  </span>
                  <span className="text-xl font-bold text-red-600">
                    {formatCurrency(selectedPlan.price)}
                  </span>
                </div>
              </div>



              {/* Proceed to Payment Button */}
              <motion.button
                disabled={loading || !selectedNetwork || phoneNumber.length !== 10}
                onClick={handleProceedToPayment}
                whileHover={{
                  scale: loading || !selectedNetwork || phoneNumber.length !== 10 ? 1 : 1.02,
                }}
                whileTap={{
                  scale: loading || !selectedNetwork || phoneNumber.length !== 10 ? 1 : 0.98,
                }}
                className={`flex gap-2 justify-center items-center px-4 py-2.5 w-full text-sm font-semibold text-white rounded-lg shadow-md transition-all duration-300 ${
                  loading || !selectedNetwork || phoneNumber.length !== 10
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-red-600 to-red-700 hover:shadow-lg"
                }`}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4" />
                )}
                {loading ? "Processing..." : "Proceed to Payment"}
              </motion.button>

              {/* Validation Messages */}
              {(!selectedNetwork || phoneNumber.length !== 10) && !loading && (
                <div className="mt-2 text-xs text-center text-gray-500">
                  {!selectedNetwork && "Please select a network"}
                  {!selectedNetwork && phoneNumber.length !== 10 && " and "}
                  {phoneNumber.length !== 10 && "enter a valid phone number"}
                </div>
              )}

              {/* Security Notice */}
              <div className="p-2.5 mt-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex gap-1.5 items-center">
                  <Shield className="w-3.5 h-3.5 text-red-600" />
                  <span className="text-xs font-medium text-red-700">
                    Secure Payment
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-red-600">
                  Your payment information is encrypted and secure
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
               
      {/* ProcessingPaymentModal commented out — now using payment gateway redirect
      {isProcessingOpen && (
        <ProcessingPaymentModal
          onComplete={() => setIsProcessingOpen(false)}
          isProcessingOpen={isProcessingOpen}
          paymentDetails={currentPayload}
          processingSteps={[
            "Initiating payment",
            "Waiting for confirmation",
            "Processing payment",
            "Verifying transaction",
          ]}
          payload={currentPayload}
        />
      )}
      */}
    </motion.div>
  );
};

export default PaymentConfirmation;
